import React from "react";
import { ButtonBase, CircularProgress, useTheme } from "@mui/material";
import { BRAND } from "../theme/index.js";

interface GlowButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  loading?: boolean;
  disabled?: boolean;
  startIcon?: React.ReactNode;
  size?: "sm" | "md" | "lg";
  type?: "button" | "submit";
}

/**
 * Primary CTA — one per page maximum.
 * Cyan outline + glow at rest; left→right fill sweep on hover.
 */
export function GlowButton({
  children,
  onClick,
  loading = false,
  disabled = false,
  startIcon,
  size = "md",
  type = "button",
}: GlowButtonProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const padding = size === "sm" ? "5px 14px" : size === "lg" ? "10px 28px" : "7px 20px";
  const fontSize = size === "sm" ? 12 : size === "lg" ? 15 : 13;

  return (
    <ButtonBase
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      sx={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding,
        fontSize,
        fontFamily: "'DM Sans', sans-serif",
        fontWeight: 600,
        borderRadius: "4px",
        border: `1.5px solid ${BRAND.cyan}`,
        color: BRAND.cyan,
        backgroundColor: "transparent",
        // Glow halo
        boxShadow: isDark
          ? `0 0 12px rgba(51,197,232,0.25), 0 0 4px rgba(51,197,232,0.15)`
          : `0 0 8px rgba(51,197,232,0.20)`,
        // Gradient sweep: left half is filled (cyan→teal), right half is transparent.
        // At rest, background is positioned to show the right (transparent) half.
        // On hover, it slides left to reveal the filled half.
        backgroundImage: `linear-gradient(90deg, ${BRAND.cyan} 0%, #1AB0D6 49%, transparent 50%)`,
        backgroundSize: "200% 100%",
        backgroundPosition: "100% 0",
        transition: "background-position 260ms ease, box-shadow 260ms ease",
        "&:hover:not(:disabled)": {
          backgroundPosition: "0% 0",
          color: "#0B0E14",
          boxShadow: isDark
            ? `0 0 20px rgba(51,197,232,0.40), 0 0 6px rgba(51,197,232,0.25)`
            : `0 0 14px rgba(51,197,232,0.35)`,
        },
        "&:disabled": {
          opacity: 0.45,
          cursor: "not-allowed",
          boxShadow: "none",
        },
      }}
    >
      {loading ? (
        <CircularProgress size={14} sx={{ color: "inherit" }} />
      ) : startIcon}
      {children}
    </ButtonBase>
  );
}
